import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { emailIntegrationAPI } from '../api';
import { toast } from 'sonner';
import { 
  Mail, Settings, RefreshCw, CheckCircle, XCircle, AlertCircle, 
  Link2, Unlink, Clock, Shield, ArrowRight, Loader2 
} from 'lucide-react';

export const EmailSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [isConnectOpen, setIsConnectOpen] = useState(false);
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    email_address: '',
    password: '',
    imap_server: 'outlook.office365.com',
    imap_port: 993,
    smtp_server: 'smtp.office365.com',
    smtp_port: 587
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, statusRes] = await Promise.allSettled([
        emailIntegrationAPI.getSettings(),
        emailIntegrationAPI.getSyncStatus()
      ]);
      
      if (settingsRes.status === 'fulfilled') {
        setConnected(settingsRes.value.data.connected);
        setSettings(settingsRes.value.data.settings);
      }
      if (statusRes.status === 'fulfilled') {
        setSyncStatus(statusRes.value.data);
      }
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await emailIntegrationAPI.createSettings(formData);
      toast.success('Email connected successfully!');
      setIsConnectOpen(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect email');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await emailIntegrationAPI.deleteSettings();
      toast.success('Email disconnected');
      setConnected(false);
      setSettings(null);
      setIsDisconnectOpen(false);
    } catch (error) {
      toast.error('Failed to disconnect email');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await emailIntegrationAPI.testConnection();
      if (response.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await emailIntegrationAPI.triggerSync();
      toast.success('Email sync started. This may take a few minutes.');
    } catch (error) {
      toast.error('Failed to start sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleAutoSync = async (enabled) => {
    try {
      await emailIntegrationAPI.updateSettings({ auto_sync_enabled: enabled });
      setSettings(prev => ({ ...prev, auto_sync_enabled: enabled }));
      toast.success(`Auto-sync ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  if (loading && !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email Integration</h1>
          <p className="text-muted-foreground">Connect your Outlook email to sync emails with contacts</p>
        </div>
      </div>

      {/* Connection Status Card */}
      <Card className={`border-l-4 ${connected ? 'border-l-emerald-500' : 'border-l-slate-300'}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-full ${connected ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                <Mail className={`h-6 w-6 ${connected ? 'text-emerald-600' : 'text-slate-500'}`} />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Outlook Email
                  {connected ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Connected</Badge>
                  ) : (
                    <Badge variant="secondary">Not Connected</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {connected 
                    ? `Connected as ${settings?.email_address}` 
                    : 'Connect your Outlook to sync emails with CRM contacts'
                  }
                </CardDescription>
              </div>
            </div>
            {connected ? (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleTestConnection}
                  disabled={testing}
                  data-testid="test-connection-btn"
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Test Connection
                </Button>
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => setIsDisconnectOpen(true)}
                  data-testid="disconnect-email-btn"
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  Disconnect
                </Button>
              </div>
            ) : (
              <Button onClick={() => setIsConnectOpen(true)} data-testid="connect-email-btn">
                <Link2 className="h-4 w-4 mr-2" />
                Connect Outlook
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Connected Settings */}
      {connected && settings && (
        <>
          {/* Sync Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Email Sync
              </CardTitle>
              <CardDescription>
                Automatically sync incoming and outgoing emails with contact records
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Sync Status Summary */}
              {syncStatus && (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-100 p-2 rounded-full">
                      <Mail className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">{syncStatus.email_count || 0} Emails Synced</p>
                      <p className="text-sm text-muted-foreground">
                        Linked to contacts, deals, and tickets
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    Every {syncStatus.sync_interval_minutes || 5} min
                  </Badge>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Auto-Sync</p>
                  <p className="text-sm text-muted-foreground">
                    Automatically check for new emails every {settings.sync_interval_minutes || 5} minutes
                  </p>
                </div>
                <Switch 
                  checked={settings.auto_sync_enabled}
                  onCheckedChange={handleToggleAutoSync}
                  data-testid="auto-sync-toggle"
                />
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <p className="font-medium">Manual Sync</p>
                  <p className="text-sm text-muted-foreground">
                    Last synced: {syncStatus?.last_sync_at 
                      ? new Date(syncStatus.last_sync_at).toLocaleString() 
                      : settings.last_sync_at 
                        ? new Date(settings.last_sync_at).toLocaleString()
                        : 'Never'
                    }
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleSync}
                  disabled={syncing}
                  data-testid="manual-sync-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Sync Now
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* How It Works */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                How Email Sync Works
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="bg-blue-100 p-2 rounded-full">
                      <Mail className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="font-medium">1. Email Received</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    New emails are fetched from your Outlook inbox and sent items
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="bg-emerald-100 p-2 rounded-full">
                      <ArrowRight className="h-4 w-4 text-emerald-600" />
                    </div>
                    <span className="font-medium">2. Auto-Match</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Emails are matched to contacts by email address
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="bg-purple-100 p-2 rounded-full">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="font-medium">3. Cross-Linked</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Emails appear on Contact, Deal, and Ticket records
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Not Connected Info */}
      {!connected && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="bg-blue-50 p-4 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold">Connect Your Outlook Email</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Sync your Outlook emails with UnifiCRM to automatically link emails to customer records. 
                All team members can view email history for complete sales redundancy.
              </p>
              <div className="flex flex-col gap-2 text-sm text-left max-w-md mx-auto">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span>Automatic email-to-contact matching</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span>Link emails to Deals and Tickets</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span>Send emails directly from CRM</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span>Team visibility for sales continuity</span>
                </div>
              </div>
              <Button onClick={() => setIsConnectOpen(true)} className="mt-4" data-testid="connect-email-cta">
                <Link2 className="h-4 w-4 mr-2" />
                Connect Outlook
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connect Dialog */}
      <Dialog open={isConnectOpen} onOpenChange={setIsConnectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Connect Outlook Email</DialogTitle>
            <DialogDescription>
              Enter your Outlook email credentials to sync emails with CRM
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConnect}>
            <div className="space-y-4 py-4">
              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  We recommend using an <strong>App Password</strong> instead of your main password. 
                  <a href="https://support.microsoft.com/en-us/account-billing/using-app-passwords-with-apps-that-don-t-support-two-step-verification-5896ed9b-4263-e681-128a-a6f2979a7944" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="text-blue-600 hover:underline ml-1"
                  >
                    Learn how to create one
                  </a>
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <Label htmlFor="email_address">Outlook Email Address</Label>
                <Input
                  id="email_address"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email_address}
                  onChange={(e) => setFormData({...formData, email_address: e.target.value})}
                  required
                  data-testid="outlook-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">App Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter app password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  data-testid="outlook-password-input"
                />
              </div>

              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Advanced Settings
                </summary>
                <div className="mt-3 space-y-3 pl-2 border-l-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="imap_server">IMAP Server</Label>
                      <Input
                        id="imap_server"
                        value={formData.imap_server}
                        onChange={(e) => setFormData({...formData, imap_server: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="imap_port">IMAP Port</Label>
                      <Input
                        id="imap_port"
                        type="number"
                        value={formData.imap_port}
                        onChange={(e) => setFormData({...formData, imap_port: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="smtp_server">SMTP Server</Label>
                      <Input
                        id="smtp_server"
                        value={formData.smtp_server}
                        onChange={(e) => setFormData({...formData, smtp_server: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="smtp_port">SMTP Port</Label>
                      <Input
                        id="smtp_port"
                        type="number"
                        value={formData.smtp_port}
                        onChange={(e) => setFormData({...formData, smtp_port: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                </div>
              </details>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsConnectOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="connect-submit-btn">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Connect
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={isDisconnectOpen} onOpenChange={setIsDisconnectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Email?</DialogTitle>
            <DialogDescription>
              This will stop syncing emails from your Outlook account. Existing synced emails will remain in the CRM.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDisconnectOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDisconnect} data-testid="confirm-disconnect-btn">
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailSettingsPage;
